import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Rocket,
  Save,
  Globe,
  Code,
  AlertCircle,
  Check,
  RefreshCw,
  Calendar,
  Palette,
  Type,
  Play,
  Square,
  Link as LinkIcon,
  Copy,
  Shield,
  MessageCircle,
  Maximize2,
  PanelRight,
  Eye,
  EyeOff,
  KeyRound,
  X,
  ExternalLink,
  Bot,
  AppWindow
} from 'lucide-react';
import {DeploymentDto, chatAgentService} from './ChatIntents/chatAgentService';

// Type Definitions
export type Theme = 'dark' | 'light';
export type ChatbotType = 'domain' | 'url' | 'embed';
export type DeploymentStatus = 'active' | 'stopped';
export type EmbedMethod = 'javascript' | 'iframe';
export type EmbedDisplayMode = 'icon' | 'full' | 'inline';

export interface DeploymentSettings {
  id?: string;
  deploymentName: string;
  chatbotType: ChatbotType;
  deploymentUrl: string;
  embedCode: string;
  status: DeploymentStatus;
  
  // Appearance
  headingText: string;
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  
  // Access Control
  accessDays: string[];
  accessStartTime: string;
  accessEndTime: string;
  timezone: string;
  
  // Welcome Message
  welcomeMessage: string;
  
  // Domain Path
  domainPath: string;
  
  // Security
  chatToken: string;
  embedToken: string;
  
  // Embed Method
  embedMethod: EmbedMethod;
  /** Icon launcher (popup), full-page embed, or inline panel */
  embedDisplayMode: EmbedDisplayMode;
  /** Required when deployment_type is WebsiteEmbed */
  websiteEmbedOrigin?: string;
}

export interface ChatAgentDeploymentProps {
  theme?: Theme;
  agentId: string;
  tenantId: string;
}

// Theme Configuration
const THEME_CONFIG = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: {
      primary: 'text-white',
      secondary: 'text-neutral-300',
      muted: 'text-neutral-400'
    },
    accent: 'bg-indigo-600',
    input: 'bg-neutral-700 border-neutral-600 text-white',
    hover: 'hover:bg-neutral-700'
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-50',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: {
      primary: 'text-gray-900',
      secondary: 'text-gray-800',
      muted: 'text-gray-700'
    },
    accent: 'bg-indigo-600',
    input: 'bg-white border-gray-300 text-gray-900',
    hover: 'hover:bg-gray-100'
  }
};

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' }
];

// API expects exactly: Mon, Tue, Wed, Thu, Fri, Sat, Sun (3-letter title-case)
const DAY_VALUE_TO_API: Record<string, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
  friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
  Mon: 'Mon', Tue: 'Tue', Wed: 'Wed', Thu: 'Thu', Fri: 'Fri', Sat: 'Sat', Sun: 'Sun'
};
const API_TO_DAY_VALUE: Record<string, string> = {
  Mon: 'monday', Tue: 'tuesday', Wed: 'wednesday', Thu: 'thursday',
  Fri: 'friday', Sat: 'saturday', Sun: 'sunday'
};
const ALLOWED_ACTIVE_DAYS_API = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function normalizeActiveDaysForApi(accessDays: string[]): string[] {
  const out: string[] = [];
  for (const d of accessDays) {
    const api = DAY_VALUE_TO_API[d];
    if (api && !out.includes(api)) out.push(api);
  }
  return out.sort((a, b) => ALLOWED_ACTIVE_DAYS_API.indexOf(a) - ALLOWED_ACTIVE_DAYS_API.indexOf(b));
}

function normalizeActiveDaysFromApi(activeDays: string[] | undefined): string[] {
  if (!Array.isArray(activeDays)) return [];
  const internalValues = DAYS_OF_WEEK.map(x => x.value);
  return activeDays
    .map(d => API_TO_DAY_VALUE[d] ?? (internalValues.includes(d) ? d : null))
    .filter((d): d is string => !!d)
    .filter((d, i, arr) => arr.indexOf(d) === i);
}

// API expects "HH:mm:ss". HTML time input gives "HH:mm".
function normalizeTimeToHHmmss(t: string | undefined): string {
  if (!t || typeof t !== 'string') return '00:00:00';
  const parts = t.trim().split(':');
  const h = parts[0]?.padStart(2, '0') || '00';
  const m = parts[1]?.padStart(2, '0') || '00';
  const s = parts[2]?.padStart(2, '0') ?? '00';
  return `${h}:${m}:${s}`;
}

const CHATBOT_TYPES = [
  { value: 'domain', label: 'Custom Domain', icon: Globe, description: 'Deploy on your own domain', disabled: true },
  { value: 'url', label: 'Direct URL', icon: LinkIcon, description: 'Shareable chatbot link', disabled: false },
  { value: 'embed', label: 'Website Embed', icon: Code, description: 'Widget, popup, or full page', disabled: false }
];

// API expects: DirectURL | CustomDomain | WebsiteEmbed
const DEPLOYMENT_TYPE_TO_API: Record<ChatbotType, string> = {
  url: 'DirectURL',
  domain: 'CustomDomain',
  embed: 'WebsiteEmbed'
};
const DEPLOYMENT_TYPE_FROM_API: Record<string, ChatbotType> = {
  DirectURL: 'url',
  CustomDomain: 'domain',
  WebsiteEmbed: 'embed'
};

const POSITION_OPTIONS = [
  { value: 'bottom-right', label: 'Bottom Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'top-left', label: 'Top Left' }
];

const EMBED_DISPLAY_MODES: {
  value: EmbedDisplayMode;
  label: string;
  description: string;
  icon: typeof MessageCircle;
}[] = [
  {
    value: 'icon',
    label: 'Icon launcher',
    description: 'Floating AI bot that opens an on-page chat panel',
    icon: MessageCircle
  },
  {
    value: 'full',
    label: 'Full embed',
    description: 'Full-page chat via iframe',
    icon: Maximize2
  },
  {
    value: 'inline',
    label: 'Inline panel',
    description: 'Side/inline chat panel via iframe',
    icon: PanelRight
  }
];

const INLINE_EMBED_SIZES = [
  { label: 'Compact', width: '360px', height: '520px' },
  { label: 'Standard', width: '100%', height: '640px' },
  { label: 'Tall', width: '100%', height: '800px' }
] as const;

const CHAT_PROD_ORIGIN = 'https://chat.selficai.com';
const CHAT_DEV_ORIGIN = 'http://localhost:5173';

/** Use localhost chat app in local/dev; production host otherwise. */
function getChatPublicOrigin(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return CHAT_DEV_ORIGIN;
    }
  }
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
    return CHAT_DEV_ORIGIN;
  }
  return CHAT_PROD_ORIGIN;
}

function getChatPublicHostLabel(): string {
  try {
    return new URL(getChatPublicOrigin()).host + '/';
  } catch {
    return 'chat.selficai.com/';
  }
}

function buildChatAgentUrl(agentId: string, query?: Record<string, string>): string {
  const base = `${getChatPublicOrigin()}/${agentId}`;
  if (!query || Object.keys(query).length === 0) return base;
  const params = new URLSearchParams(query);
  return `${base}?${params.toString()}`;
}

/** Inline SVG markup for the floating AI bot launcher (no emoji). */
const EMBED_AI_BOT_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<rect x="4.5" y="7.5" width="15" height="12" rx="3.5" stroke="#fff" stroke-width="1.6"/>' +
  '<circle cx="9.25" cy="13" r="1.35" fill="#fff"/>' +
  '<circle cx="14.75" cy="13" r="1.35" fill="#fff"/>' +
  '<path d="M12 7.5V4.75" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/>' +
  '<circle cx="12" cy="3.75" r="1.15" fill="#a5b4fc"/>' +
  '<path d="M8.5 19.5v1.25M15.5 19.5v1.25" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/>' +
  '<path d="M2.75 12.25h1.5M19.75 12.25h1.5" stroke="#c7d2fe" stroke-width="1.5" stroke-linecap="round"/>' +
  '</svg>';

function buildEmbedSnippet(opts: {
  masterId: string;
  embedToken: string;
  position: string;
  headingText: string;
  welcomeMessage: string;
  displayMode: EmbedDisplayMode;
  size?: { width: string; height: string };
}): string {
  const {
    masterId,
    embedToken,
    position,
    headingText,
    welcomeMessage,
    displayMode,
    size = INLINE_EMBED_SIZES[1]
  } = opts;

  const chatUrl = buildChatAgentUrl(masterId, {
    token: embedToken,
    mode: displayMode
  });
  const title = headingText || 'AI Assistant';

  if (displayMode === 'icon') {
    const isLeft = position.includes('left');
    const isTop = position.includes('top');
    const side = isLeft ? 'left' : 'right';
    const edge = isTop ? 'top' : 'bottom';
    const ariaLabel = headingText || 'Chat with AI assistant';

    return `<!-- Selfic AI Chatbot — Icon launcher (on-page panel) -->
<script>
(function () {
  if (document.getElementById("selfic-ai-launcher")) return;

  var CHAT_URL = ${JSON.stringify(chatUrl)};
  var TITLE = ${JSON.stringify(title)};
  var SIDE = "${side}";
  var EDGE = "${edge}";
  var BTN = 60;
  var MARGIN = 20;
  var GAP = 14;
  var NORMAL = { w: 420, h: 680 };
  var LARGE = { w: 560, h: 780 };
  var open = false;
  /** 0 = normal, 1 = large, 2 = near full page */
  var sizeMode = 0;

  var root = document.createElement("div");
  root.id = "selfic-ai-launcher";

  var btn = document.createElement("button");
  btn.type = "button";
  btn.setAttribute("aria-label", ${JSON.stringify(ariaLabel)});
  btn.setAttribute("aria-expanded", "false");
  btn.innerHTML = ${JSON.stringify(EMBED_AI_BOT_ICON_SVG)};
  btn.style.cssText = [
    "position:fixed",
    EDGE + ":" + MARGIN + "px",
    SIDE + ":" + MARGIN + "px",
    "z-index:100000",
    "width:" + BTN + "px",
    "height:" + BTN + "px",
    "border-radius:9999px",
    "border:none",
    "cursor:pointer",
    "background:linear-gradient(145deg,#4f46e5 0%,#6366f1 55%,#7c3aed 100%)",
    "box-shadow:0 10px 28px rgba(79,70,229,.45)",
    "display:flex",
    "align-items:center",
    "justify-content:center",
    "padding:0",
    "transition:transform .18s ease"
  ].join(";");
  btn.onmouseenter = function () { btn.style.transform = "scale(1.06)"; };
  btn.onmouseleave = function () { btn.style.transform = "scale(1)"; };

  var panel = document.createElement("div");
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-label", TITLE);
  panel.style.cssText = [
    "display:none",
    "position:fixed",
    "z-index:99999",
    "flex-direction:column",
    "border-radius:16px",
    "overflow:hidden",
    "box-shadow:0 20px 50px rgba(0,0,0,.35)",
    "background:#0a0a0a",
    "border:1px solid rgba(255,255,255,.08)"
  ].join(";");

  var header = document.createElement("div");
  header.style.cssText = "flex:0 0 auto;height:44px;display:flex;align-items:center;justify-content:space-between;gap:8px;padding:0 10px 0 14px;background:linear-gradient(90deg,#4338ca,#4f46e5);color:#fff;";

  var titleEl = document.createElement("div");
  titleEl.style.cssText = "display:flex;align-items:center;gap:8px;min-width:0;font:600 13px/1.2 system-ui,-apple-system,sans-serif;";
  var badge = document.createElement("span");
  badge.style.cssText = "display:inline-flex;width:22px;height:22px;border-radius:9999px;background:rgba(255,255,255,.18);align-items:center;justify-content:center;flex-shrink:0";
  badge.innerHTML = ${JSON.stringify(
    EMBED_AI_BOT_ICON_SVG.replace(/width="28"/g, 'width="14"').replace(/height="28"/g, 'height="14"')
  )};
  var titleText = document.createElement("span");
  titleText.style.cssText = "overflow:hidden;text-overflow:ellipsis;white-space:nowrap";
  titleText.textContent = TITLE;
  titleEl.appendChild(badge);
  titleEl.appendChild(titleText);

  var actions = document.createElement("div");
  actions.style.cssText = "display:flex;align-items:center;gap:4px;flex-shrink:0;";

  function makeHeaderBtn(label, svg) {
    var b = document.createElement("button");
    b.type = "button";
    b.setAttribute("aria-label", label);
    b.title = label;
    b.innerHTML = svg;
    b.style.cssText = "width:30px;height:30px;border:none;border-radius:8px;background:transparent;color:#fff;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;padding:0;";
    b.onmouseenter = function () { b.style.background = "rgba(255,255,255,.16)"; };
    b.onmouseleave = function () { b.style.background = "transparent"; };
    return b;
  }

  var SVG_LARGE = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3"/></svg>';
  var SVG_PAGE = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/></svg>';
  var SVG_RESTORE = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3"/></svg>';
  var SVG_CLOSE = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>';

  var largeBtn = makeHeaderBtn("Larger panel", SVG_LARGE);
  var pageBtn = makeHeaderBtn("Near full page", SVG_PAGE);
  var closeBtn = makeHeaderBtn("Close", SVG_CLOSE);
  actions.appendChild(largeBtn);
  actions.appendChild(pageBtn);
  actions.appendChild(closeBtn);
  header.appendChild(titleEl);
  header.appendChild(actions);

  var frame = document.createElement("iframe");
  frame.src = CHAT_URL;
  frame.title = "Selfic AI Chat";
  frame.allow = "microphone; camera";
  frame.style.cssText = "flex:1 1 auto;width:100%;height:100%;border:none;background:#0a0a0a;min-height:0;";

  panel.appendChild(header);
  panel.appendChild(frame);

  function getPageSize() {
    return {
      w: Math.max(640, Math.floor(window.innerWidth - MARGIN * 2)),
      h: Math.max(480, Math.floor(window.innerHeight - MARGIN * 2))
    };
  }

  function currentSize() {
    if (sizeMode === 2) return getPageSize();
    if (sizeMode === 1) {
      return {
        w: Math.min(LARGE.w, window.innerWidth - MARGIN * 2),
        h: Math.min(LARGE.h, window.innerHeight - MARGIN * 2 - BTN - GAP)
      };
    }
    return {
      w: Math.min(NORMAL.w, window.innerWidth - MARGIN * 2),
      h: Math.min(NORMAL.h, window.innerHeight - MARGIN * 2 - BTN - GAP)
    };
  }

  function placePanel() {
    var size = currentSize();
    panel.style.width = size.w + "px";
    panel.style.height = size.h + "px";
    panel.style.maxWidth = "calc(100vw - " + (MARGIN * 2) + "px)";
    panel.style.left = "";
    panel.style.right = "";
    panel.style.top = "";
    panel.style.bottom = "";
    panel.style.borderRadius = sizeMode === 2 ? "12px" : "16px";

    if (sizeMode === 2) {
      panel.style.maxHeight = "calc(100vh - " + (MARGIN * 2) + "px)";
      panel.style.left = "50%";
      panel.style.top = "50%";
      panel.style.transform = "translate(-50%, -50%)";
      panel.style[SIDE] = "";
      panel.style[EDGE] = "";
    } else {
      panel.style.transform = "";
      panel.style.maxHeight = "calc(100vh - " + (MARGIN * 2 + BTN + GAP) + "px)";
      panel.style[SIDE] = MARGIN + "px";
      panel.style[EDGE] = (MARGIN + BTN + GAP) + "px";
    }
  }

  function setOpen(next) {
    open = next;
    panel.style.display = open ? "flex" : "none";
    btn.setAttribute("aria-expanded", open ? "true" : "false");
    if (open) placePanel();
    else sizeMode = 0;
    syncHeader();
  }

  function applySizeMode(mode) {
    sizeMode = mode;
    if (!open) setOpen(true);
    else placePanel();
    syncHeader();
  }

  function syncHeader() {
    var largeOn = sizeMode === 1;
    var pageOn = sizeMode === 2;
    largeBtn.title = largeOn ? "Restore default size" : "Larger panel";
    largeBtn.setAttribute("aria-label", largeBtn.title);
    largeBtn.innerHTML = largeOn ? SVG_RESTORE : SVG_LARGE;
    pageBtn.title = pageOn ? "Restore default size" : "Near full page width";
    pageBtn.setAttribute("aria-label", pageBtn.title);
    pageBtn.innerHTML = pageOn ? SVG_RESTORE : SVG_PAGE;
  }

  btn.addEventListener("click", function () { setOpen(!open); });
  closeBtn.addEventListener("click", function () { setOpen(false); });
  largeBtn.addEventListener("click", function () {
    applySizeMode(sizeMode === 1 ? 0 : 1);
  });
  pageBtn.addEventListener("click", function () {
    applySizeMode(sizeMode === 2 ? 0 : 2);
  });
  window.addEventListener("resize", function () { if (open) placePanel(); });

  root.appendChild(panel);
  root.appendChild(btn);
  document.body.appendChild(root);
})();
</script>`;
  }

  if (displayMode === 'full') {
    return `<!-- Selfic AI Chatbot — Full embed (iframe) -->
<iframe
  src="${chatUrl}"
  title="${title.replace(/"/g, '&quot;')}"
  width="100%"
  height="100%"
  frameborder="0"
  allow="microphone; camera"
  style="border: none; width: 100%; height: 100%; min-height: 100vh; display: block;"
></iframe>`;
  }

  // Inline / side panel — iframe at selected size
  return `<!-- Selfic AI Chatbot — Inline / side panel (iframe) -->
<iframe
  src="${chatUrl}"
  title="${title.replace(/"/g, '&quot;')}"
  width="${size.width}"
  height="${size.height}"
  frameborder="0"
  allow="microphone; camera"
  style="border: none; border-radius: 12px; width: ${size.width}; height: ${size.height}; max-width: 100%; display: block; box-shadow: 0 12px 32px rgba(0,0,0,.18);"
></iframe>`;
}

// Compact Section Header Component
const SectionHeader: React.FC<{
  icon: React.ElementType;
  title: string;
  theme: Theme;
}> = ({ icon: Icon, title, theme }) => {
  const colors = THEME_CONFIG[theme];
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className={`p-1.5 ${colors.accent} rounded-lg`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <h3 className={`text-sm font-semibold ${colors.text.primary}`}>
        {title}
      </h3>
    </div>
  );
};

/** Modal preview of how each embed mode appears on a host page */
const EmbedSamplePreviewModal: React.FC<{
  mode: EmbedDisplayMode;
  theme: Theme;
  chatUrl: string;
  headingText: string;
  welcomeMessage: string;
  position: DeploymentSettings['position'];
  inlineSize: { width: string; height: string };
  onClose: () => void;
}> = ({
  mode,
  theme,
  chatUrl,
  headingText,
  welcomeMessage,
  position,
  inlineSize,
  onClose
}) => {
  const colors = THEME_CONFIG[theme];
  const modeMeta = EMBED_DISPLAY_MODES.find((m) => m.value === mode) || EMBED_DISPLAY_MODES[0];
  const [iconOpen, setIconOpen] = useState(mode === 'icon');
  /** 0 = normal, 1 = large, 2 = near full page */
  const [iconSizeMode, setIconSizeMode] = useState(0);

  const launcherCorner =
    position.includes('left') ? 'left-5' : 'right-5';
  const launcherEdge =
    position.includes('top') ? 'top-5' : 'bottom-5';
  const panelOffset = position.includes('top') ? 'top-[5.5rem]' : 'bottom-[5.5rem]';

  const iconPanelClass =
    iconSizeMode === 2
      ? 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-1.5rem)] h-[calc(100%-1.5rem)]'
      : iconSizeMode === 1
        ? `${launcherCorner} ${panelOffset} w-[min(520px,calc(100%-2.5rem))] h-[min(500px,calc(100%-7.5rem))]`
        : `${launcherCorner} ${panelOffset} w-[min(400px,calc(100%-2.5rem))] h-[min(440px,calc(100%-7.5rem))]`;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${modeMeta.label} sample preview`}
    >
      <div
        className={`w-full max-w-4xl max-h-[90vh] flex flex-col rounded-xl border ${colors.border} ${colors.surface} shadow-2xl overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between gap-3 px-4 py-3 border-b ${colors.border}`}>
          <div className="min-w-0">
            <p className={`text-sm font-semibold ${colors.text.primary}`}>
              Sample — {modeMeta.label}
            </p>
            <p className={`text-[10px] mt-0.5 ${colors.text.muted}`}>
              {modeMeta.description}
              {mode === 'icon' ? ' · on-page panel' : ' · iframe'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href={chatUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] rounded border ${colors.border} ${colors.secondary} ${colors.hover} ${colors.text.secondary}`}
            >
              <ExternalLink className="w-3 h-3" />
              Open chat URL
            </a>
            <button
              type="button"
              onClick={onClose}
              className={`p-1.5 rounded ${colors.hover} ${colors.text.muted}`}
              aria-label="Close preview"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className={`flex-1 overflow-auto p-4 ${colors.background}`}>
          {mode === 'icon' && (
            <div className="relative mx-auto max-w-3xl h-[560px] rounded-lg border border-neutral-600 overflow-hidden bg-gradient-to-br from-neutral-100 to-neutral-200 dark:from-neutral-800 dark:to-neutral-900">
              <div className="h-9 flex items-center gap-1.5 px-3 bg-neutral-300/80 dark:bg-neutral-950/80 border-b border-neutral-400/40">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
                <span className="ml-3 text-[10px] text-neutral-600 dark:text-neutral-400 truncate">
                  yourwebsite.com — sample page
                </span>
              </div>
              <div className="p-6 space-y-3">
                <div className="h-4 w-2/5 rounded bg-neutral-400/40" />
                <div className="h-3 w-4/5 rounded bg-neutral-400/30" />
                <div className="h-3 w-3/5 rounded bg-neutral-400/25" />
                <div className="mt-6 grid grid-cols-3 gap-3">
                  <div className="h-20 rounded-lg bg-neutral-400/20" />
                  <div className="h-20 rounded-lg bg-neutral-400/20" />
                  <div className="h-20 rounded-lg bg-neutral-400/20" />
                </div>
                <p className="text-[10px] text-neutral-500 pt-4">
                  Opens an on-page panel (not a new window). Use larger or near full-page from the header.
                </p>
              </div>

              {iconOpen && (
                <div
                  className={`absolute ${iconPanelClass} z-10 flex flex-col rounded-2xl overflow-hidden shadow-2xl border border-neutral-600 bg-neutral-950 transition-all`}
                >
                  <div className="h-11 flex items-center justify-between px-3 bg-gradient-to-r from-indigo-700 to-indigo-600 text-white text-xs font-medium flex-shrink-0">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="inline-flex w-5 h-5 rounded-full bg-white/20 items-center justify-center flex-shrink-0">
                        <Bot className="w-3 h-3" />
                      </span>
                      <span className="truncate">{headingText || 'AI Assistant'}</span>
                    </span>
                    <span className="flex items-center gap-0.5 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setIconSizeMode((m) => (m === 1 ? 0 : 1))}
                        className="p-1.5 rounded-md hover:bg-white/15"
                        title={iconSizeMode === 1 ? 'Restore' : 'Larger panel'}
                      >
                        <Maximize2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setIconSizeMode((m) => (m === 2 ? 0 : 2))}
                        className="p-1.5 rounded-md hover:bg-white/15"
                        title={iconSizeMode === 2 ? 'Restore' : 'Near full page'}
                      >
                        <AppWindow className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIconOpen(false);
                          setIconSizeMode(0);
                        }}
                        className="p-1.5 rounded-md hover:bg-white/15"
                        aria-label="Close chat"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  </div>
                  <iframe
                    src={chatUrl}
                    title="Icon launcher sample"
                    className="flex-1 w-full min-h-0 border-0 bg-neutral-950"
                    allow="microphone; camera"
                  />
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  setIconOpen((v) => !v);
                  if (iconOpen) setIconSizeMode(0);
                }}
                className={`absolute ${launcherCorner} ${launcherEdge} z-20 w-[60px] h-[60px] rounded-full bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-600 text-white shadow-xl hover:scale-105 transition-transform flex items-center justify-center`}
                aria-label={headingText || 'Open AI chat'}
                title={welcomeMessage || headingText}
              >
                <Bot className="w-7 h-7" />
              </button>
            </div>
          )}

          {mode === 'full' && (
            <div className="mx-auto max-w-3xl h-[560px] rounded-lg border border-neutral-600 overflow-hidden flex flex-col bg-neutral-950">
              <div className="h-9 flex items-center gap-1.5 px-3 bg-neutral-900 border-b border-neutral-700">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
                <span className="ml-3 text-[10px] text-neutral-400 truncate">
                  Full embed · iframe fills the page
                </span>
              </div>
              <iframe
                src={chatUrl}
                title="Full embed sample"
                className="flex-1 w-full border-0 bg-neutral-950"
                allow="microphone; camera"
              />
            </div>
          )}

          {mode === 'inline' && (
            <div className="mx-auto max-w-3xl rounded-lg border border-neutral-600 overflow-hidden bg-gradient-to-br from-neutral-100 to-neutral-200 dark:from-neutral-800 dark:to-neutral-900">
              <div className="h-9 flex items-center gap-1.5 px-3 bg-neutral-300/80 dark:bg-neutral-950/80 border-b border-neutral-400/40">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
                <span className="ml-3 text-[10px] text-neutral-600 dark:text-neutral-400 truncate">
                  Inline panel · {inlineSize.width} × {inlineSize.height}
                </span>
              </div>
              <div className="p-5 grid grid-cols-1 md:grid-cols-[1fr_minmax(240px,1.1fr)] gap-4">
                <div className="space-y-3">
                  <div className="h-4 w-3/5 rounded bg-neutral-400/40" />
                  <div className="h-3 w-full rounded bg-neutral-400/30" />
                  <div className="h-3 w-4/5 rounded bg-neutral-400/25" />
                  <div className="h-24 rounded-lg bg-neutral-400/20" />
                  <p className={`text-[10px] ${colors.text.muted}`}>
                    Side/inline chat loads in an iframe at the selected size.
                  </p>
                </div>
                <div
                  className="rounded-xl overflow-hidden border border-neutral-600 shadow-lg bg-neutral-950 mx-auto w-full"
                  style={{
                    maxWidth: inlineSize.width === '100%' ? '100%' : inlineSize.width,
                    height: inlineSize.height
                  }}
                >
                  <iframe
                    src={chatUrl}
                    title="Inline panel sample"
                    className="w-full h-full border-0"
                    allow="microphone; camera"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Deployment Component
const ChatAgentDeployment: React.FC<ChatAgentDeploymentProps> = ({
  theme = 'dark',
  agentId,
  tenantId
}) => {
  const colors = THEME_CONFIG[theme];
  const { chatbotId } = useParams<{ chatbotId?: string }>();
  const masterId = chatbotId || agentId;

  const [settings, setSettings] = useState<DeploymentSettings>({
    deploymentName: '',
    chatbotType: 'url',
    deploymentUrl: '',
    embedCode: '',
    status: 'stopped',
    
    headingText: 'Chat with us',
    position: 'bottom-right',
    
    accessDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    accessStartTime: '09:00',
    accessEndTime: '17:00',
    timezone: 'UTC',
    
    welcomeMessage: 'Hello! How can I help you today?',
    
    domainPath: '',
    chatToken: '',
    embedToken: '',
    embedMethod: 'javascript',
    embedDisplayMode: 'icon',
    websiteEmbedOrigin: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deploymentNameError, setDeploymentNameError] = useState<string | null>(null);
  const [showEmbedSecret, setShowEmbedSecret] = useState(false);
  const [inlineEmbedSize, setInlineEmbedSize] = useState<(typeof INLINE_EMBED_SIZES)[number]>(
    INLINE_EMBED_SIZES[1]
  );
  const [embedPreviewMode, setEmbedPreviewMode] = useState<EmbedDisplayMode | null>(null);

  useEffect(() => {
    if (masterId) loadDeployment();
    else setLoading(false);
  }, [masterId]);

  const generateToken = (type: string): string => {
    const prefix = type === 'chat' ? 'ctk_' : 'etk_';
    const randomString = Math.random().toString(36).substring(2, 15) + 
                        Math.random().toString(36).substring(2, 15);
    return prefix + randomString;
  };

  const loadDeployment = async () => {
    if (!masterId) return;
    setLoading(true);
    setError(null);

    try {
      const response = await chatAgentService.getDeploymentsByAgent(masterId);
      
      const list = Array.isArray(response?.data) ? response.data : (response?.data?.data ?? []);
      if (response?.success === true && list.length > 0) {
        const d = list[0];
        const id = d.id ?? d.deployment_id;
        const chatToken = d.chat_token || generateToken('chat');
        const embedToken = d.embed_token || generateToken('embed');
        const headingText = typeof d.heading_text === 'string' ? d.heading_text : (d.heading_text?.en ?? 'Chat with us');
        const welcomeMessage = typeof d.welcome_message === 'string' ? d.welcome_message : (d.welcome_message?.en ?? '');

        setSettings({
          id,
          deploymentName: d.deployment_name || '',
          chatbotType: (d.deployment_type ? DEPLOYMENT_TYPE_FROM_API[d.deployment_type] : undefined) ?? (d.chatbot_type as DeploymentSettings['chatbotType']) ?? 'url',
          deploymentUrl: d.deployment_url || d.direct_url || d.custom_domain || '',
          embedCode: d.embed_code || '',
          status: (d.status || 'stopped') as DeploymentSettings['status'],
          headingText,
          position: (d.position || 'bottom-right') as DeploymentSettings['position'],
          accessDays: normalizeActiveDaysFromApi(d.active_days || d.access_days),
          accessStartTime: (d.active_start_time || d.access_start_time || '09:00').slice(0, 5),
          accessEndTime: (d.active_end_time || d.access_end_time || '17:00').slice(0, 5),
          timezone: d.timezone || 'UTC',
          welcomeMessage,
          domainPath: d.url_path || d.domain_path || '',
          chatToken,
          embedToken,
          embedMethod: (d.embed_method || 'javascript') as DeploymentSettings['embedMethod'],
          embedDisplayMode: (d.embed_display_mode || d.embed_mode || 'icon') as EmbedDisplayMode,
          websiteEmbedOrigin: d.website_embed_origin || ''
        });
      } else {
        setSettings(prev => ({
          ...prev,
          chatToken: generateToken('chat'),
          embedToken: generateToken('embed')
        }));
      }
    } catch (err) {
      console.error('Error loading deployment:', err);
      setError('Failed to load deployment settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setDeploymentNameError(null);
    setSuccessMessage(null);

    const nameTrimmed = settings.deploymentName?.trim();
    if (!nameTrimmed) {
      setDeploymentNameError('Deployment name is required.');
      setError('Please enter a deployment name. This field is required to save.');
      return;
    }

    const startTime = normalizeTimeToHHmmss(settings.accessStartTime);
    const endTime = normalizeTimeToHHmmss(settings.accessEndTime);
    if (startTime >= endTime) {
      setError('Active start time must be earlier than active end time.');
      return;
    }

    const deploymentType = DEPLOYMENT_TYPE_TO_API[settings.chatbotType];
    if (settings.chatbotType === 'domain') {
      const customDomain = settings.deploymentUrl?.trim();
      if (!customDomain) {
        setError('Custom domain is required for Custom Domain deployment.');
        return;
      }
    }
    if (settings.chatbotType === 'embed') {
      const origin = settings.websiteEmbedOrigin?.trim();
      if (!origin) {
        setError('Website embed origin is required for Website Embed deployment.');
        return;
      }
    }

    setSaving(true);

    try {
      let deploymentUrl = settings.deploymentUrl;
      let embedCode = settings.embedCode;

      if (settings.chatbotType === 'url') {
        deploymentUrl = buildChatAgentUrl(masterId);
      } else if (settings.chatbotType === 'domain') {
        deploymentUrl = settings.deploymentUrl;
      }

      if (settings.chatbotType === 'embed') {
        embedCode = buildEmbedSnippet({
          masterId,
          embedToken: settings.embedToken,
          position: settings.position,
          headingText: settings.headingText,
          welcomeMessage: settings.welcomeMessage,
          displayMode: settings.embedDisplayMode || 'icon',
          size: inlineEmbedSize
        });
      }

      const basePayload: DeploymentDto = {

        id: masterId,
        deployment_id: settings.id,
        chatagent_master_id: masterId,
        agent_id: masterId,
        tenant_id: tenantId,
        deployment_name: settings.deploymentName,
        environment: 'production',
        status: settings.status,
        deployment_type: deploymentType,
        active_days: normalizeActiveDaysForApi(settings.accessDays),
        active_start_time: startTime,
        active_end_time: endTime,
        timezone: settings.timezone?.trim() || 'UTC',
        heading_text: settings.headingText,
        welcome_message: settings.welcomeMessage,
        chat_token: settings.chatToken,
        deployed_by: localStorage.getItem('user_uid') || 'system'
      };

      if (settings.chatbotType === 'url') {
        basePayload.deployment_url = deploymentUrl;
        basePayload.direct_url = deploymentUrl;
        basePayload.base_domain = new URL(getChatPublicOrigin()).host;
        basePayload.url_path = masterId.trim() || undefined;
      } else if (settings.chatbotType === 'domain') {
        basePayload.custom_domain = settings.deploymentUrl?.trim();
        basePayload.deployment_url = settings.deploymentUrl?.trim();
      } else if (settings.chatbotType === 'embed') {
        basePayload.website_embed_origin = settings.websiteEmbedOrigin?.trim();
        basePayload.embed_token = settings.embedToken;
        basePayload.embed_method = settings.embedDisplayMode === 'icon' ? 'javascript' : 'iframe';
        basePayload.embed_display_mode = settings.embedDisplayMode || 'icon';
        basePayload.embed_code = embedCode;
        basePayload.position = settings.position;
      }

      const response = await chatAgentService.upsertDeployment(basePayload);

      if (response?.success === true) {
        setSuccessMessage('Deployment saved successfully');
        setSettings(prev => ({
          ...prev,
          deploymentUrl,
          embedCode
        }));
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err: any) {
      console.error('Error saving deployment:', err);
      setError(err?.response?.data?.message || 'Failed to save deployment settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof DeploymentSettings, value: any) => {
    setSettings(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'embedDisplayMode') {
        const mode = value as EmbedDisplayMode;
        next.embedMethod = mode === 'icon' ? 'javascript' : 'iframe';
      }
      return next;
    });
    if (field === 'deploymentName') setDeploymentNameError(null);
    if (field === 'deploymentName') setError(null);
  };

  const getLiveEmbedCode = () =>
    buildEmbedSnippet({
      masterId,
      embedToken: settings.embedToken,
      position: settings.position,
      headingText: settings.headingText,
      welcomeMessage: settings.welcomeMessage,
      displayMode: settings.embedDisplayMode || 'icon',
      size: inlineEmbedSize
    });

  const toggleDay = (day: string) => {
    setSettings(prev => ({
      ...prev,
      accessDays: prev.accessDays.includes(day)
        ? prev.accessDays.filter(d => d !== day)
        : [...prev.accessDays, day]
    }));
  };

  const regenerateToken = (type: 'chat' | 'embed') => {
    const newToken = generateToken(type);
    if (type === 'chat') {
      setSettings(prev => ({ ...prev, chatToken: newToken }));
    } else {
      setSettings(prev => ({ ...prev, embedToken: newToken }));
    }
    setSuccessMessage(`${type === 'chat' ? 'Chat' : 'Embed'} token regenerated`);
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setSuccessMessage(`${type} copied!`);
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  if (loading) {
    return (
      <div className={`h-full ${colors.background} flex items-center justify-center`}>
        <div className="text-center">
          <RefreshCw className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-3" />
          <p className={`text-sm ${colors.text.secondary}`}>Loading deployment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full ${colors.background} overflow-auto`}>
      {/* Compact Header */}
      <div className={`px-5 py-3 border-b ${colors.border} ${colors.primary} sticky top-0 z-10 backdrop-blur-sm bg-opacity-95`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-base font-semibold ${colors.text.primary}`}>Deployment Settings</h1>
            <p className={`text-xs ${colors.text.muted} mt-0.5`}>
              Configure and deploy your chatbot
            </p>
          </div>

          <div className="flex items-center gap-2">
            {successMessage && (
              <div className="flex items-center gap-1.5 text-green-500 text-xs font-medium">
                <Check className="w-3.5 h-3.5" />
                {successMessage}
              </div>
            )}

            <button
              onClick={loadDeployment}
              className={`px-2.5 py-1.5 ${colors.secondary} border ${colors.border} ${colors.hover} text-xs rounded transition-colors flex items-center gap-1.5`}
              disabled={loading}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            <button
              onClick={handleSave}
              disabled={saving || !settings.deploymentName?.trim()}
              className={`px-3 py-1.5 ${colors.accent} hover:bg-indigo-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white text-xs rounded transition-colors flex items-center gap-1.5 font-medium`}
              title={!settings.deploymentName?.trim() ? 'Deployment name is required' : undefined}
            >
              {saving ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5" />
                  Save & Deploy
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 max-w-7xl mx-auto">
        
        {/* Error Message */}
        {error && (
          <div className={`mb-4 p-3 ${theme === 'light' ? 'bg-red-50 border-red-200' : 'bg-red-900/20 border-red-700'} border rounded-lg flex items-start gap-2`}>
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className={`text-xs font-medium ${theme === 'light' ? 'text-red-800' : 'text-red-200'}`}>
                {error}
              </p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className={`mb-4 p-3 ${theme === 'light' ? 'bg-green-50 border-green-200' : 'bg-green-900/20 border-green-700'} border rounded-lg flex items-start gap-2`}>
            <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className={`text-sm font-medium ${theme === 'light' ? 'text-green-800' : 'text-green-200'}`}>
                {successMessage}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          
          {/* Left Column - Main Settings */}
          <div className="lg:col-span-2 space-y-4">
            
            {/* Basic Configuration */}
            <div className={`${colors.surface} rounded-lg p-4 border ${colors.border}`}>
              <SectionHeader icon={Rocket} title="Basic Configuration" theme={theme} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                <div>
                  <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                    Deployment Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={settings.deploymentName}
                    onChange={(e) => handleInputChange('deploymentName', e.target.value)}
                    className={`w-full ${colors.input} border rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      deploymentNameError ? 'border-red-500 focus:ring-red-500' : ''
                    }`}
                    placeholder="Production Deployment"
                    aria-invalid={!!deploymentNameError}
                    aria-describedby={deploymentNameError ? 'deployment-name-error' : undefined}
                  />
                  {deploymentNameError && (
                    <p id="deployment-name-error" className="mt-1 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      {deploymentNameError}
                    </p>
                  )}
                </div>

                <div>
                  <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                    Status
                  </label>
                  <button
                    onClick={() => handleInputChange('status', settings.status === 'active' ? 'stopped' : 'active')}
                    className={`w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      settings.status === 'active'
                        ? 'bg-green-600 text-white'
                        : 'bg-red-600 text-white'
                    }`}
                  >
                    {settings.status === 'active' ? (
                      <>
                        <Play className="w-3.5 h-3.5" />
                        Active
                      </>
                    ) : (
                      <>
                        <Square className="w-3.5 h-3.5" />
                        Stopped
                      </>
                    )}
                  </button>
                </div>

                <div className="md:col-span-2">
                  <label className={`block text-xs font-medium ${colors.text.primary} mb-2`}>
                    Deployment Type
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {CHATBOT_TYPES.map((type) => {
                      const Icon = type.icon;
                      const isDisabled = type.disabled;
                      const isSelected = settings.chatbotType === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          disabled={isDisabled}
                          onClick={() => !isDisabled && handleInputChange('chatbotType', type.value)}
                          className={`p-2.5 border rounded-lg text-left transition-all ${
                            isDisabled
                              ? 'opacity-50 cursor-not-allowed border-neutral-600 bg-neutral-800/50'
                              : isSelected
                              ? `border-indigo-500 ${colors.accent} bg-opacity-10`
                              : `${colors.border} ${colors.secondary} ${colors.hover}`
                          }`}
                          title={isDisabled ? 'Coming soon' : type.description}
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            <Icon className={`w-4 h-4 ${isSelected ? 'text-indigo-400' : colors.text.muted}`} />
                            <span className={`text-xs font-medium ${colors.text.primary}`}>
                              {type.label}
                            </span>
                          </div>
                          <p className={`text-[10px] ${colors.text.muted}`}>
                            {isDisabled ? 'Coming soon' : type.description}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {settings.chatbotType === 'domain' && (
                  <div className="md:col-span-2">
                    <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                      Custom Domain
                    </label>
                    <input
                      type="text"
                      value={settings.deploymentUrl}
                      onChange={(e) => handleInputChange('deploymentUrl', e.target.value)}
                      className={`w-full ${colors.input} border rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      placeholder="chat.yourdomain.com"
                    />
                  </div>
                )}

                {settings.chatbotType === 'url' && (
                  <div className="md:col-span-2">
                    <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                      URL Path
                    </label>
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1.5 ${colors.secondary} border ${colors.border} rounded text-sm ${colors.text.muted} whitespace-nowrap`}>
                        {getChatPublicHostLabel()}
                      </span>
                      <input
                        type="text"
                        value={masterId}
                        disabled
                        className={`flex-1 ${colors.input} border rounded px-2.5 py-1.5 text-sm opacity-70 cursor-not-allowed`}
                        placeholder="agent guid"
                      />
                    </div>
                    <p className={`text-[10px] ${colors.text.muted} mt-1`}>
                      Auto-filled from URL route agent GUID
                    </p>
                  </div>
                )}

                {settings.chatbotType === 'embed' && (
                  <div className="md:col-span-2">
                    <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                      Allowed Website Origin
                    </label>
                    <input
                      type="url"
                      value={settings.websiteEmbedOrigin || ''}
                      onChange={(e) => handleInputChange('websiteEmbedOrigin', e.target.value)}
                      className={`w-full ${colors.input} border rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      placeholder="https://www.yourwebsite.com"
                    />
                    <p className={`text-[10px] ${colors.text.muted} mt-1`}>
                      Only this origin can load the embed. Include protocol (https://).
                    </p>
                  </div>
                )}

              </div>
            </div>

            {/* Appearance Settings */}
            <div className={`${colors.surface} rounded-lg p-4 border ${colors.border}`}>
              <SectionHeader icon={Palette} title="Appearance" theme={theme} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                <div className="md:col-span-2">
                  <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                    Heading Text
                  </label>
                  <input
                    type="text"
                    value={settings.headingText}
                    onChange={(e) => handleInputChange('headingText', e.target.value)}
                    className={`w-full ${colors.input} border rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    placeholder="Chat with us"
                  />
                </div>

                {settings.chatbotType === 'embed' && (
                  <>
                    <div className="md:col-span-2">
                      <label className={`block text-xs font-medium ${colors.text.primary} mb-2`}>
                        Embed Display Mode
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {EMBED_DISPLAY_MODES.map((mode) => {
                          const Icon = mode.icon;
                          const isSelected = settings.embedDisplayMode === mode.value;
                          return (
                            <div
                              key={mode.value}
                              className={`p-3 border rounded-lg text-left transition-all ${
                                isSelected
                                  ? `border-indigo-500 ${colors.accent} bg-opacity-10 ring-1 ring-indigo-500/40`
                                  : `${colors.border} ${colors.secondary}`
                              }`}
                            >
                              <button
                                type="button"
                                onClick={() => handleInputChange('embedDisplayMode', mode.value)}
                                className="w-full text-left"
                              >
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <Icon className={`w-4 h-4 ${isSelected ? 'text-indigo-400' : colors.text.muted}`} />
                                  <span className={`text-xs font-medium ${colors.text.primary}`}>
                                    {mode.label}
                                  </span>
                                </div>
                                <p className={`text-[10px] leading-relaxed ${colors.text.muted}`}>
                                  {mode.description}
                                </p>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  handleInputChange('embedDisplayMode', mode.value);
                                  setEmbedPreviewMode(mode.value);
                                }}
                                className={`mt-2.5 w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded text-[10px] font-medium border ${colors.border} ${colors.hover} ${colors.text.secondary}`}
                              >
                                <Eye className="w-3 h-3" />
                                View sample
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {settings.embedDisplayMode === 'icon' && (
                      <div className="md:col-span-2">
                        <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                          Launcher Position
                        </label>
                        <select
                          value={settings.position}
                          onChange={(e) => handleInputChange('position', e.target.value)}
                          className={`w-full ${colors.input} border rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                        >
                          {POSITION_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {settings.embedDisplayMode === 'inline' && (
                      <div className="md:col-span-2">
                        <label className={`block text-xs font-medium ${colors.text.primary} mb-2`}>
                          Panel Size
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          {INLINE_EMBED_SIZES.map((size) => {
                            const isSelected = inlineEmbedSize.label === size.label;
                            return (
                              <button
                                key={size.label}
                                type="button"
                                onClick={() => setInlineEmbedSize(size)}
                                className={`px-2.5 py-2 border rounded-lg text-center transition-all ${
                                  isSelected
                                    ? 'border-indigo-500 bg-indigo-600/10'
                                    : `${colors.border} ${colors.secondary} ${colors.hover}`
                                }`}
                              >
                                <span className={`block text-xs font-medium ${colors.text.primary}`}>
                                  {size.label}
                                </span>
                                <span className={`block text-[10px] mt-0.5 ${colors.text.muted}`}>
                                  {size.width} × {size.height}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}

              </div>
            </div>

            {/* Welcome Message */}
            <div className={`${colors.surface} rounded-lg p-4 border ${colors.border}`}>
              <SectionHeader icon={Type} title="Welcome Message" theme={theme} />

              <div>
                <textarea
                  value={settings.welcomeMessage}
                  onChange={(e) => handleInputChange('welcomeMessage', e.target.value)}
                  className={`w-full ${colors.input} border rounded px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none`}
                  rows={3}
                  placeholder="Hello! How can I help you today?"
                />
              </div>
            </div>

            {/* Security Tokens */}
            <div className={`${colors.surface} rounded-lg p-4 border ${colors.border}`}>
              <SectionHeader icon={Shield} title="Security Tokens" theme={theme} />

              <div className="space-y-3">
                <div>
                  <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                    Chat Token
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={settings.chatToken}
                      readOnly
                      className={`flex-1 ${colors.input} border rounded px-2.5 py-1.5 text-xs font-mono`}
                    />
                    <button
                      onClick={() => copyToClipboard(settings.chatToken, 'Token')}
                      className={`px-2.5 py-1.5 ${colors.secondary} border ${colors.border} ${colors.hover} rounded`}
                      title="Copy"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => regenerateToken('chat')}
                      className={`px-2.5 py-1.5 ${colors.secondary} border ${colors.border} ${colors.hover} rounded`}
                      title="Regenerate"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <p className={`text-[10px] ${colors.text.muted} mt-1`}>
                    Required for API authentication
                  </p>
                </div>

                {settings.chatbotType === 'embed' && (
                  <div>
                    <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                      Embed Secret Key
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <KeyRound className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${colors.text.muted}`} />
                        <input
                          type={showEmbedSecret ? 'text' : 'password'}
                          value={settings.embedToken}
                          readOnly
                          className={`w-full ${colors.input} border rounded pl-8 pr-2.5 py-1.5 text-xs font-mono`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowEmbedSecret((v) => !v)}
                        className={`px-2.5 py-1.5 ${colors.secondary} border ${colors.border} ${colors.hover} rounded`}
                        title={showEmbedSecret ? 'Hide' : 'Show'}
                      >
                        {showEmbedSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(settings.embedToken, 'Secret key')}
                        className={`px-2.5 py-1.5 ${colors.secondary} border ${colors.border} ${colors.hover} rounded`}
                        title="Copy"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => regenerateToken('embed')}
                        className={`px-2.5 py-1.5 ${colors.secondary} border ${colors.border} ${colors.hover} rounded`}
                        title="Regenerate"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className={`text-[10px] ${colors.text.muted} mt-1`}>
                      Included in your embed snippet. Regenerate if this key is exposed.
                    </p>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Right Column - Access Control & Deployment Info */}
          <div className="space-y-4">
            
            {/* Access Schedule */}
            <div className={`${colors.surface} rounded-lg p-4 border ${colors.border}`}>
              <SectionHeader icon={Calendar} title="Access Schedule" theme={theme} />

              <div className="space-y-3">
                
                <div>
                  <label className={`block text-xs font-medium ${colors.text.primary} mb-2`}>
                    Active Days
                  </label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {DAYS_OF_WEEK.map(day => (
                      <button
                        key={day.value}
                        onClick={() => toggleDay(day.value)}
                        className={`px-2 py-1.5 rounded text-[10px] font-medium transition-colors ${
                          settings.accessDays.includes(day.value)
                            ? `${colors.accent} text-white`
                            : `${colors.secondary} ${colors.text.secondary} ${colors.hover}`
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                    Active Hours
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={`block text-[10px] ${colors.text.muted} mb-1`}>Start</label>
                      <input
                        type="time"
                        value={settings.accessStartTime}
                        onChange={(e) => handleInputChange('accessStartTime', e.target.value)}
                        className={`w-full ${colors.input} border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      />
                    </div>
                    <div>
                      <label className={`block text-[10px] ${colors.text.muted} mb-1`}>End</label>
                      <input
                        type="time"
                        value={settings.accessEndTime}
                        onChange={(e) => handleInputChange('accessEndTime', e.target.value)}
                        className={`w-full ${colors.input} border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                    Timezone
                  </label>
                  <select
                    value={settings.timezone}
                    onChange={(e) => handleInputChange('timezone', e.target.value)}
                    className={`w-full ${colors.input} border rounded px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern (ET)</option>
                    <option value="America/Chicago">Central (CT)</option>
                    <option value="America/Los_Angeles">Pacific (PT)</option>
                    <option value="Europe/London">London (GMT)</option>
                    <option value="Asia/Dubai">Dubai (GST)</option>
                    <option value="Asia/Riyadh">Riyadh (AST)</option>
                  </select>
                </div>

              </div>
            </div>

            {/* Deployment Info - Direct URL */}
            {settings.chatbotType === 'url' && settings.deploymentUrl && (
              <div className={`${colors.surface} rounded-lg p-4 border ${colors.border}`}>
                <SectionHeader icon={LinkIcon} title="Direct URL Access" theme={theme} />

                <div className="space-y-3">
                  <div>
                    <label className={`block text-[10px] ${colors.text.muted} mb-1`}>
                      Shareable Link
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={settings.deploymentUrl}
                        readOnly
                        className={`flex-1 ${colors.input} border rounded px-2.5 py-1.5 text-xs`}
                      />
                      <button
                        onClick={() => copyToClipboard(settings.deploymentUrl, 'URL')}
                        className={`px-2.5 py-1.5 ${colors.accent} hover:bg-indigo-700 text-white rounded`}
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className={`p-3 ${colors.secondary} border ${colors.border} rounded-lg`}>
                    <h4 className={`text-xs font-semibold ${colors.text.primary} mb-2`}>
                      Quick Start Guide
                    </h4>
                    <ol className={`text-[10px] ${colors.text.secondary} space-y-1.5 list-decimal list-inside`}>
                      <li>Share the URL with your users</li>
                      <li>Users can access the chatbot directly in their browser</li>
                      <li>Use the chat token for API integration</li>
                      <li>Monitor conversations in the dashboard</li>
                    </ol>
                  </div>

                  <div className={`p-3 ${theme === 'light' ? 'bg-blue-50 border-blue-200' : 'bg-blue-900/20 border-blue-700'} border rounded-lg`}>
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <p className={`text-[10px] ${theme === 'light' ? 'text-blue-800' : 'text-blue-200'}`}>
                        The chatbot is accessible 24/7 unless restricted by the access schedule above.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Deployment Info - Embed Code */}
            {settings.chatbotType === 'embed' && (
              <div className={`${colors.surface} rounded-lg p-4 border ${colors.border}`}>
                <SectionHeader icon={Code} title="Embed Snippet" theme={theme} />

                <div className="space-y-3">
                  <div className={`flex items-start gap-2.5 p-3 rounded-lg border ${colors.border} ${colors.secondary}`}>
                    {(() => {
                      const activeMode =
                        EMBED_DISPLAY_MODES.find((m) => m.value === settings.embedDisplayMode) ||
                        EMBED_DISPLAY_MODES[0];
                      const ModeIcon = activeMode.icon;
                      return (
                        <>
                          <div className={`p-1.5 rounded-md ${colors.accent} flex-shrink-0`}>
                            <ModeIcon className="w-3.5 h-3.5 text-white" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className={`text-xs font-medium ${colors.text.primary}`}>
                              {activeMode.label}
                            </p>
                            <p className={`text-[10px] mt-0.5 ${colors.text.muted}`}>
                              {activeMode.description}
                              {settings.embedDisplayMode === 'icon'
                                ? ` · ${settings.position.replace(/-/g, ' ')}`
                                : settings.embedDisplayMode === 'inline'
                                  ? ` · ${inlineEmbedSize.width} × ${inlineEmbedSize.height}`
                                  : ' · 100% viewport'}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setEmbedPreviewMode(settings.embedDisplayMode || 'icon')}
                            className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-1.5 rounded text-[10px] font-medium border ${colors.border} ${colors.hover} ${colors.text.secondary}`}
                          >
                            <Eye className="w-3 h-3" />
                            Sample
                          </button>
                        </>
                      );
                    })()}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className={`text-[10px] font-medium ${colors.text.muted}`}>
                        {settings.embedDisplayMode === 'icon'
                          ? 'Paste before </body>'
                          : settings.embedDisplayMode === 'full'
                            ? 'Paste on a dedicated full-page container'
                            : 'Paste where the side/inline panel should appear'}
                      </label>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(getLiveEmbedCode(), 'Embed code')}
                        className={`px-2 py-1 ${colors.accent} hover:bg-indigo-700 text-white rounded text-[10px] flex items-center gap-1`}
                      >
                        <Copy className="w-3 h-3" />
                        Copy Code
                      </button>
                    </div>
                    <pre className={`${colors.input} border rounded-lg p-3 text-[10px] font-mono overflow-x-auto whitespace-pre-wrap max-h-56 overflow-y-auto`}>
                      <code>{getLiveEmbedCode()}</code>
                    </pre>
                  </div>

                  <div className={`p-3 ${colors.secondary} border ${colors.border} rounded-lg`}>
                    <h4 className={`text-xs font-semibold ${colors.text.primary} mb-2`}>
                      Install steps
                    </h4>
                    <div className={`text-[10px] ${colors.text.secondary} space-y-2`}>
                      {settings.embedDisplayMode === 'icon' ? (
                        <>
                          <div className="flex gap-2">
                            <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>1</span>
                            <p>Copy the snippet above</p>
                          </div>
                          <div className="flex gap-2">
                            <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>2</span>
                            <p>Paste it just before the closing <code className={`${colors.input} px-1 rounded`}>&lt;/body&gt;</code> tag</p>
                          </div>
                          <div className="flex gap-2">
                            <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>3</span>
                            <p>Publish — the AI bot opens an on-page chat panel (no new window)</p>
                          </div>
                        </>
                      ) : settings.embedDisplayMode === 'full' ? (
                        <>
                          <div className="flex gap-2">
                            <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>1</span>
                            <p>Copy the full-page iframe snippet</p>
                          </div>
                          <div className="flex gap-2">
                            <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>2</span>
                            <p>Paste it on a dedicated chat page (or a full-width container)</p>
                          </div>
                          <div className="flex gap-2">
                            <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>3</span>
                            <p>Ensure the host page origin matches your allowed website origin</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex gap-2">
                            <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>1</span>
                            <p>Copy the inline iframe snippet</p>
                          </div>
                          <div className="flex gap-2">
                            <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>2</span>
                            <p>Paste it in your layout where the side/panel chat should appear</p>
                          </div>
                          <div className="flex gap-2">
                            <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>3</span>
                            <p>Adjust Compact / Standard / Tall size as needed</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className={`p-3 ${theme === 'light' ? 'bg-amber-50 border-amber-200' : 'bg-amber-900/20 border-amber-700'} border rounded-lg`}>
                    <div className="flex items-start gap-2">
                      <Shield className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <p className={`text-[10px] ${theme === 'light' ? 'text-amber-800' : 'text-amber-300'}`}>
                        The embed secret key is baked into this snippet. Restrict it to your allowed origin and regenerate if it leaks.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Deployment Info - Domain */}
            {settings.chatbotType === 'domain' && settings.deploymentUrl && (
              <div className={`${colors.surface} rounded-lg p-4 border ${colors.border}`}>
                <SectionHeader icon={Globe} title="Domain Deployment" theme={theme} />

                <div className="space-y-3">
                  <div>
                    <label className={`block text-[10px] ${colors.text.muted} mb-1`}>
                      Custom Domain
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={settings.deploymentUrl}
                        readOnly
                        className={`flex-1 ${colors.input} border rounded px-2.5 py-1.5 text-xs`}
                      />
                      <button
                        onClick={() => copyToClipboard(settings.deploymentUrl, 'Domain')}
                        className={`px-2.5 py-1.5 ${colors.accent} hover:bg-indigo-700 text-white rounded`}
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className={`p-3 ${colors.secondary} border ${colors.border} rounded-lg`}>
                    <h4 className={`text-xs font-semibold ${colors.text.primary} mb-2`}>
                      DNS Configuration
                    </h4>
                    <div className={`text-[10px] ${colors.text.secondary} space-y-1.5`}>
                      <p>Add a CNAME record pointing to:</p>
                      <code className={`block ${colors.input} px-2 py-1 rounded mt-1`}>
                        chatbot.selficai.com
                      </code>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {embedPreviewMode && (
        <EmbedSamplePreviewModal
          key={embedPreviewMode}
          mode={embedPreviewMode}
          theme={theme}
          chatUrl={buildChatAgentUrl(masterId)}
          headingText={settings.headingText}
          welcomeMessage={settings.welcomeMessage}
          position={settings.position}
          inlineSize={inlineEmbedSize}
          onClose={() => setEmbedPreviewMode(null)}
        />
      )}
    </div>
  );
};

export default ChatAgentDeployment;